import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios';
import { FaEdit } from "react-icons/fa";
import { TbSortAscending2Filled } from "react-icons/tb";
import { toast } from "react-toastify";
import { Tooltip } from 'react-tooltip';
import { BsFiletypeCsv } from "react-icons/bs";

import AddCategoryPopup from './addCategoryPopup';
import EditCategoryPopup from './editCategoryPopup';
import Spinner from '../../components/spinner';
import AddedBy from "../../components/addedBy";
import Pagination from '../../components/pagination';
import FilterBar from '../../components/filterBar';
import SortBy from '../../components/sortBy';
import { useFilterChips } from '../../components/filterBar/useFilterChips';
import { useLanguage } from '../../context/languageContext';
import GenerateCSV from '../../components/popup/generateCSV';
import NoContent from '../../components/notFound/noBooks';
import { formatMongoDBTime } from '../../utills/dateAndTime';
import { normalizeCoverImage, convertToCloudFrontUrl, htmlToPlainText , fetchAllGenresByLangOptions , fetchAllActorNames } from '../../helper';
import { Can, usePermissions } from "../../context/permissionContext";
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";


function Books() {
    const { adminInfo, isShowCSVModalVisible, setIsShowCSVModalVisible, handleCloseCSV, handleGenerateCSV } = useLanguage();
    const apiParamsRef = useRef(null);

    const { can } = usePermissions();
    // Only a publisher may flip a draft live; everyone else sees the state
    // read-only rather than a button that always 403s.
    const canPublishBooks = can('books', 'publish');
    const [togglingId, setTogglingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [bookId, setBookId] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isAddCategoryModalVisible, setIsAddCategoryModalVisible] = useState(false);
    const [isEditCategoryModalVisible, setIsEditCategoryModalVisible] = useState(false);
    const [selectAllBooks, setSelectAllBooks] = useState(false);
    const [failedImageUrls, setFailedImageUrls] = useState(new Set());
    const [authorImagesMap, setAuthorImagesMap] = useState({});
    const [failedAuthorImageUrls, setFailedAuthorImageUrls] = useState(new Set());

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalNumOfBooks, setTotalNumOfBooks] = useState(0);

    // Search state

    // Sorting state
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    // The Sort by control. Sent on every request, so it is the list's order
    // of record; the column arrow below keeps itself in step with it.
    const [sortBy, setSortBy] = useState('latestCreated');

    const totalPages = Math.ceil(totalNumOfBooks / itemsPerPage);


    // The chip row under the search box. `chipFilters` is already in the

    // shape the endpoint reads, so it merges straight into `apiFilters`.

    const filters = useFilterChips({ supportsMatchAny: true, stickyField: 'bookName' });

    const apiFilters = {
        currentPage,
        itemsPerPage,
        langCode: "en",
        sortOrder: sortOrder,
        sortBy,
    // LAST on purpose: the single-field search above writes '' into every
    // field it is not searching, so a spread placed above it is wiped.
    ...filters.chipFilters,
    matchMode: filters.matchMode,
    };


  // Genre is a managed list, so the filter offers the stored names rather than
  // asking anyone to spell them. Fetched once and shared by the Genre chip.
  const [genreNames, setGenreNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllGenresByLangOptions('en', adminInfo.token).then((genres) => {
      if (!cancelled) setGenreNames(genres.map((g) => g.genreName));
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);


  // The names behind Created by / Edited by, so those filters are picked from
  // the team rather than spelled from memory.
  const [actorNames, setActorNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllActorNames(adminInfo.token).then((names) => {
      if (!cancelled) setActorNames(names);
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

    const filterOptions = [
        { value: 'bookName', label: 'Book Name' },
        { value: 'authorName', label: 'Author' },
        { value: 'genreName', label: 'Genre', suggestions: genreNames },
        { value: 'createdAt', label: 'Created on' },
        { value: 'updatedAt', label: 'Updated on' },
        { value: 'createdBy', label: 'Created by', suggestions: actorNames },
        { value: 'updatedBy', label: 'Edited by', suggestions: actorNames }
    ];

    useEffect(() => {
        setCurrentPage(1);
    }, [itemsPerPage, sortOrder, sortBy, filters.signature]);

    useEffect(() => {
        if (!adminInfo?.token) {
            return;
        }

        document.title = "Penspace - Books";
        const keyValues = JSON.stringify({ currentPage, itemsPerPage, sortOrder, sortBy, filters: filters.signature });

        if (apiParamsRef.current === keyValues) {
            return;
        }

        apiParamsRef.current = keyValues;
        fetchAllBooksCategoriesData();
    }, [adminInfo, currentPage, itemsPerPage, sortOrder, sortBy, filters.signature]);

    const handleCategorySelect = (seletedUserId) => {
        if (selectedCategories.includes(seletedUserId)) {
            setSelectedCategories((prevState) => prevState.filter(productId => productId !== seletedUserId));
        } else {
            setSelectedCategories((prevState) => [...prevState, seletedUserId]);
        }
    };

    const handleSelectAllBooks = () => {
        if (selectAllBooks) {
            setSelectedCategories([]);
        } else {
            setSelectedCategories(categories.map((book) => book._id));
        }
        setSelectAllBooks((prevSelectAll) => !prevSelectAll);
    };

    const fetchAuthorImages = async (authorIds) => {
        if (!authorIds || authorIds.length === 0) return {};

        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            // Fetch all favorite authors to get their images
            const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/favorite-author/getall`, { 
                filters: { currentPage: 1, itemsPerPage: 1000, langCode: "en" }
            }, { headers });

            if (response.status && response.data?.favoriteAuthorsDocs) {
                const imagesMap = {};
                response.data.favoriteAuthorsDocs.forEach(author => {
                    if (author._id && author.image) {
                        const normalizedImage = normalizeCoverImage(author.image);
                        if (normalizedImage.uploadedImageUrl) {
                            imagesMap[author._id.toString()] = normalizedImage.uploadedImageUrl;
                        }
                    }
                });
                return imagesMap;
            }
        } catch (error) {
            console.error("Error fetching author images:", error);
        }
        return {};
    };

    // Publish / unpublish. THIS is the publish action — the create endpoint
    // forces status 0 for anyone without the right, so without this a lister's
    // book stays an invisible draft forever.
    const handleToggleStatus = async (book) => {
        const next = book.status === 0 ? 1 : 0;
        setTogglingId(book._id);
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${process.env.REACT_APP_API_ROOT}/api/admin/books/update/status/${book._id}`,
                { status: next },
                { headers }
            );
            if (response.status) {
                toast.success(response.message);
                await fetchAllBooksCategoriesData();
            } else {
                toast.error(response.error || response.message);
            }
        } catch (error) {
            toast.error(error?.response?.data?.error || 'Could not update status.');
        } finally {
            setTogglingId(null);
        }
    };

    const fetchAllBooksCategoriesData = async () => {
        setLoading(true);

        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/books/getall`, { filters: apiFilters }, { headers });

            if (response.data) {
                const normalizedBooks = response.data.allBooksDocs.map(book => ({
                    ...book,
                    coverImage: normalizeCoverImage(book.coverImage)
                }));
                setCategories(normalizedBooks);
                setTotalNumOfBooks(response.data.totalCount || 0);

                const authorIds = new Set();
                normalizedBooks.forEach(book => {
                    const translations = Array.isArray(book.translations) ? book.translations : [];
                    const enTranslation = translations.find(t => t.langCode === "en");

                    if (enTranslation?.author && Array.isArray(enTranslation.author)) {
                        enTranslation.author.forEach(author => {
                            if (author.authorId) {
                                authorIds.add(author.authorId);
                            }
                        });
                    }
                });

                if (authorIds.size > 0) {
                    const imagesMap = await fetchAuthorImages(Array.from(authorIds));
                    setAuthorImagesMap(imagesMap);
                }
            };
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setTimeout(() => setLoading(false), 300);
        }
    };

    const handleDeleteBooksCategories = async () => {
        try {
            if (selectedCategories.length > 0) {
                const headers = { Authorization: `Bearer ${adminInfo.token}` };
                const what = selectedCategories.length === 1 ? "this book" : `${selectedCategories.length} books`;
                if (!(await confirmCascadeDelete({ source: "book", ids: selectedCategories, what, headers }))) return;

                const response = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/books/delete`, { categoryIds: selectedCategories }, { headers });

                if (response.data.status) {
                    toast.success(response.data.message || "Book has been deleted.");
                    setSelectedCategories([]);
                    setSelectAllBooks(false);
                    fetchAllBooksCategoriesData();
                }
            } else {
                toast.info("Select atleast 1 book to delete");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleAddCategory = () => {
        setIsAddCategoryModalVisible(true);
    };

    const handleEditCategoryPopup = (bookId) => {
        setBookId(bookId);
        setIsEditCategoryModalVisible(true);
    };

    const fetchNewCategories = () => {
        setIsAddCategoryModalVisible(false);
        setIsEditCategoryModalVisible(false);
        fetchAllBooksCategoriesData();
    };

    const closeAddCatgoryModalOpen = () => {
        setIsAddCategoryModalVisible(false);
        setSelectAllBooks(false);
    };

    const closeEditCatgoryModalOpen = () => {
        setIsEditCategoryModalVisible(false);
        setSelectAllBooks(false);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };


    const handleSort = () => {
        setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
        // Keeps the arrow live now that Sort by decides the order.
        setSortBy(prev => (prev === 'oldestCreated' ? 'latestCreated' : 'oldestCreated'));
    };


    return (
        <React.Fragment>
            <React.Fragment>
                <div className="heading">
                    <h4>
                        {totalNumOfBooks > 1 ? `Books (${totalNumOfBooks})` : `Book (${totalNumOfBooks})`}
                    </h4>
                    <div className='d-flex gap-2'>
                        {selectedCategories.length > 0 ? (
                            <Can feature="books" action="delete"><button type="button" className="btn btn-outline-danger" onClick={handleDeleteBooksCategories}>Delete</button></Can>
                        ) : (
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-outline-dark" onClick={() => setIsShowCSVModalVisible(true)}>Generate CSV <BsFiletypeCsv /></button>
                                <Can feature="books" action="create"><button type="button" className="btn btn-dark" onClick={handleAddCategory}>Add Book</button></Can>
                            </div>
                        )}
                    </div>
                </div>
                <div className='d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center'>
                  <SortBy value={sortBy} onChange={setSortBy} />
                    {totalPages > 1 && (
                        <div className='d-flex justify-content-center justify-content-lg-end w-100 w-lg-auto ms-lg-auto'>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalNumOfBooks}
                                itemsPerPage={itemsPerPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                showItemsPerPage={true}
                                showTotalItems={true}
                            />
                        </div>
                    )}
                </div>
                <FilterBar
                  supportsMatchAny
                options={filterOptions}
                chips={filters.chips}
                matchMode={filters.matchMode}
                onMatchModeChange={filters.setMatchMode}
                onAdd={filters.addChip}
                onUpdate={filters.updateChip}
                onRemove={filters.removeChip}
                onClear={filters.clearChips}
                />

                {loading ? (
                    <Spinner />
                ) : (
                    <div className="table_scrollbar table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>
                                      <input
                                        className="box form-check-input"
                                        type="checkbox"
                                        checked={selectAllBooks}
                                        onChange={handleSelectAllBooks}
                                      />
                                    </th>
                                    <th>S.no</th>
                                    <th>Image</th>
                                    <th>Book Name
                                        <TbSortAscending2Filled
                                            size={18}
                                            style={{ cursor: 'pointer', marginLeft: '3px' }}
                                            onClick={handleSort}
                                            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                                        />
                                    </th>
                                    <th>Author</th>
                                    <th>Tagline</th>
                                    <th>Genre</th>
                                    <th>About Book</th>
                                    <th>Created on</th>
                                    <th>Updated on</th>
                                    <th>Added by</th>
                                    {/* Kept deliberately, though the spec's column
                                        list does not name it: this switch is the
                                        ONLY way to publish a book — the edit form
                                        has no status control — so dropping it would
                                        strand every draft with no way to go live. */}
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories && categories.length > 0 ? (
                                    categories.length > 0 && categories.map((book, index) => {
                                        const isCategoriesSelected = selectedCategories.some((id) => id === book._id);
                                        const showSelectedLanguage = book.translations;
                                        const globalIndex = (currentPage - 1) * itemsPerPage + index;
                                        
                                        return (
                                            book && (
                                                <tr key={book._id}>
                                                    <td className="checkBox">
                                                        <input
                                                            className={`box form-check-input`}
                                                            type="checkbox"
                                                            checked={isCategoriesSelected}
                                                            onChange={() => handleCategorySelect(book._id)}
                                                        />
                                                    </td>
                                                    <td className="index text-center">{globalIndex + 1}</td>
                                                    <td className="image">
                                                        {book.coverImage?.uploadedImageUrl && !failedImageUrls.has(book.coverImage.uploadedImageUrl) ? (
                                                            <img 
                                                                src={book.coverImage.uploadedImageUrl} 
                                                                alt="" 
                                                                size={24}
                                                                onError={(e) => {
                                                                    const brokenUrl = book.coverImage.uploadedImageUrl;
                                                                    console.error('[Book Cover Image Error] AccessDenied or failed to load:', {
                                                                        url: brokenUrl,
                                                                        bookId: book._id,
                                                                        bookTitle: showSelectedLanguage?.title || 'Unknown'
                                                                    });
                                                                    setFailedImageUrls(prev => new Set([...prev, brokenUrl]));
                                                                }}
                                                            />
                                                        ) : (
                                                            <p className="image-skeleton" aria-hidden="true"><span>NO COVER</span></p>
                                                        )}
                                                    </td>
                                                    <td className="max_width">{showSelectedLanguage.title}</td>
                                                    <td className="max_width">
                                                        {showSelectedLanguage.author?.map((item) => {
                                                            const authorImageUrl = item.authorId ? authorImagesMap[item.authorId] : null;
                                                            const hasValidImage = authorImageUrl && !failedAuthorImageUrls.has(authorImageUrl);
                                                            
                                                            return (
                                                                <li className="list_styled d-flex align-items-center gap-2" key={item.authorId || item.authorName}>
                                                                    {hasValidImage ? (
                                                                        <img 
                                                                            src={convertToCloudFrontUrl(authorImageUrl)} 
                                                                            alt={item.authorName}
                                                                            style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }}
                                                                            onError={(e) => {
                                                                                const brokenUrl = authorImageUrl;
                                                                                console.error('[Author Image Error] Failed to load:', {
                                                                                    url: brokenUrl,
                                                                                    authorId: item.authorId,
                                                                                    authorName: item.authorName
                                                                                });
                                                                                setFailedAuthorImageUrls(prev => new Set([...prev, brokenUrl]));
                                                                                e.target.style.display = 'none';
                                                                            }}
                                                                        />
                                                                    ) : null}
                                                                    <span>{item.authorName}</span>
                                                                </li>
                                                            );
                                                        })}
                                                    </td>
                                                                                                        {/* <td className="mid_width text-center">{book.rating || " NL "}</td> */}
                                                    <td className="tooltip_content">
                                                        <span
                                                            data-tooltip-id={`tagline-tooltip-${book._id}`}
                                                            data-tooltip-content={showSelectedLanguage.tagline}
                                                            data-tooltip-place="top"
                                                        >
                                                            {showSelectedLanguage.tagline || " NL "}
                                                        </span>
                                                        <Tooltip
                                                            className="custom-tooltip"
                                                            id={`tagline-tooltip-${book._id}`}
                                                            delayShow={200}
                                                            offset={10}
                                                            float={true}
                                                        />
                                                    </td>
                                                    <td className="max_width">
                                                        {showSelectedLanguage?.genre?.map((item) => (
                                                            <li className="list_styled" key={item.genreId || item.genreName}>{item.genreName}</li>
                                                        ))}
                                                    </td>
                                                    {/* "About the book" is authored in Quill, so what is
                                                        stored is HTML. Rendered straight it read
                                                        "<p><em>The Boys: Definitive...", and the tooltip
                                                        carried the whole document, empty-paragraph runs
                                                        and all. */}
                                                    <td className="tooltip_content">
                                                        <span
                                                            data-tooltip-id={`summary-tooltip-${book._id}`}
                                                            data-tooltip-content={htmlToPlainText(showSelectedLanguage?.summary)}
                                                            data-tooltip-place="top"
                                                        >
                                                            {htmlToPlainText(showSelectedLanguage?.summary)}
                                                        </span>
                                                        <Tooltip
                                                            className="custom-tooltip"
                                                            id={`summary-tooltip-${book._id}`}
                                                            delayShow={200}
                                                            offset={10}
                                                            float={true}
                                                        />
                                                    </td>
                                                    <td className="min_width">{formatMongoDBTime(book.createdAt)}</td>
                                                    <td className="min_width">{book.updatedAt ? formatMongoDBTime(book.updatedAt) : "—"}</td>
                                                    <td><AddedBy record={book} /></td>
                                                    <td className="mid_width text-center">
                                                        {/* Draft = created by someone without `publish` (see draftGate).
                                                            Without this column a draft is indistinguishable from a live
                                                            book, and nobody knows there is anything to review. */}
                                                        <button
                                                            type="button"
                                                            className={book.status === 0 ? 'status_pill status_draft' : 'status_pill status_live'}
                                                            disabled={!canPublishBooks || togglingId === book._id}
                                                            title={canPublishBooks
                                                                ? (book.status === 0 ? 'Publish this book' : 'Move back to drafts')
                                                                : 'You do not have permission to publish'}
                                                            onClick={() => canPublishBooks && handleToggleStatus(book)}
                                                        >
                                                            {togglingId === book._id ? '…' : (book.status === 0 ? 'Draft' : 'Live')}
                                                        </button>
                                                    </td>
                                                    <td className="action">
                                                        <div onClick={() => handleEditCategoryPopup(book._id)}>
                                                            <FaEdit size={20} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        );
                                    })
                                ) : (
                                    <NoContent title="books" colSpan={13} />
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </React.Fragment>

            <AddCategoryPopup
                isModalVisible={isAddCategoryModalVisible}
                setIsAddCategoryModalVisible={setIsAddCategoryModalVisible}
                closeModal={closeAddCatgoryModalOpen}
                onSuccess={fetchNewCategories}
            />

            <EditCategoryPopup
                bookId={bookId}
                isModalVisible={isEditCategoryModalVisible}
                closeModal={closeEditCatgoryModalOpen}
                onSuccess={fetchNewCategories}
            />

            <GenerateCSV
                title="books"
                isShowCSVModalVisible={isShowCSVModalVisible}
                handleCloseCSV={handleCloseCSV}
                handleGenerateCSV={() => handleGenerateCSV("books", apiFilters)}
            />
        </React.Fragment>
    );
}

export default Books
